/**
 * @file     SignInForFullDetails.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Shown only on the redacted (signed-out) listing record, inside the
 *           title block: one line saying what signing in adds, and a Sign in
 *           button that returns the person to this listing.
 */
'use client';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { useTranslation } from 'react-i18next';
import { hubColors } from '@/components/hub/hubKitStyles';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { propertyDetailContent as C } from '../../pageContent';

interface Props {
  signInHref: string;
}

export function SignInForFullDetails({ signInHref }: Props) {
  const { t } = useTranslation();
  return (
    <Box
      data-signed-out-line
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        p: 3,
        borderRadius: `${layoutTokens.radius.md}px`,
        backgroundColor: hubColors.surfaceSunken,
      }}
    >
      <Box component="svg" aria-hidden viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" sx={{ flexShrink: 0, color: hubColors.textSecondary }}>
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </Box>
      <Box component="p" sx={{ m: 0, flex: 1, minWidth: 0, fontSize: 14, lineHeight: 1.45, color: hubColors.textSecondary }}>
        <Box component="strong" sx={{ fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
          {t(C.fullDetails.title.key, { defaultValue: C.fullDetails.title.defaultValue })}
        </Box>{' '}
        {t(C.fullDetails.body.key, { defaultValue: C.fullDetails.body.defaultValue })}
      </Box>
      <ButtonBase
        component="a"
        href={signInHref}
        sx={{
          flexShrink: 0,
          minHeight: 40,
          px: 3,
          borderRadius: `${layoutTokens.radius.md}px`,
          border: `1px solid ${hubColors.borderStrong}`,
          backgroundColor: hubColors.surfaceCard,
          color: hubColors.textPrimary,
          fontSize: 14,
          fontWeight: fontWeightTokens.medium,
          whiteSpace: 'nowrap',
          '&.Mui-focusVisible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
        }}
      >
        {t(C.fullDetails.cta.key, { defaultValue: C.fullDetails.cta.defaultValue })}
      </ButtonBase>
    </Box>
  );
}
